import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Badge } from 'react-bootstrap';
import { FaClipboardList, FaLock, FaLockOpen } from 'react-icons/fa';
import { hasPermission } from '../../hooks/usePermission';
import { getRole } from '../../utils/role';
import { handleReportNavigation } from '../../utils/mobileNavigation';

const ReportsTable = ({ reports, handleLock, startIndex = 0, handleOpenWaterModal }) => {
  const navigate = useNavigate();
  const role = getRole();

  const canView = hasPermission('reports:view');
  const canLock = hasPermission('reports:lock');
  const canUpdateWater = (role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'FIELD_OFFICER';
  const isSuperAdmin = role === 'SUPPORT';
  
  // Specific permissions
  const canFollowup = hasPermission('reports:followup');

  const format = (value) =>
    value !== null && value !== undefined && value !== ''
      ? String(value).toUpperCase()
      : '-';

  // Helper function to format date to DD/MM/YY
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(Date.parse(dateString));
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '-';
    }
  };

  return (
    <div className="table-responsive">
      <table className={`table table-striped text-center table-borderless ${reports.length > 1 ? 'align-middle' : ''}`}>
        <thead className="table-dark align-top">
          <tr>
            <th>S.No.</th>
            <th>REPORT ID</th>
            <th>SAMPLE ID</th>
            <th>FARM NAME</th>
            <th>FARM SIZE</th>
            {hasPermission('reports:test_partner') && <th>TEST PARTNER</th>}
            <th>LOCATION</th>
            <th>PREVIOUS CROP</th>
            <th>NEXT CROP</th>
            <th>DATE</th>
            <th>STATUS</th>
            <th>ACTION</th>
            {canFollowup && <th>FOLLOW UP</th>}
          </tr>
        </thead>

        <tbody>
          {reports.length > 0 ? (
            reports.map((report, index) => (
              <tr key={report.report_id}>
                <td>{report.row_number || (startIndex + index + 1)}</td>
                <td>{format(report.report_id)}</td>
                <td>{format(report.sample_id)}</td>
                <td>{format(report.farm_name)}</td>
                <td>
                  {format(report.farm_size)} {format(report.farm_size_unit)}
                </td>

                {hasPermission('reports:source') && <td>{format(report.source)}</td>}
                <td>{format(report.city)}</td>

                <td>{format(report.previous_crop)}</td>
                <td>{format(report.next_crop)}</td>
                <td>
                  {formatDate(report.created_at)}
                </td>

                {/* STATUS (Visible to all) */}
                <td>
                  {report.report_status === 'R' ? (
                    <Badge bg="warning" text="dark">{isSuperAdmin ? 'Needs Review' : 'Under Review'}</Badge>
                  ) : report.report_status === 'S' ? (
                    <Badge bg="success">Good</Badge>
                  ) : (
                    <Badge bg="secondary">{report.report_status || '-'}</Badge>
                  )}
                </td>

                {/* ACTION (View/Lock) */}
                <td>
                  {role === 'FIELD_OFFICER' && report.is_locked ? (
                    <div className="d-flex justify-content-center">
                      <FaLock color="red" size={20} title="Locked" />
                    </div>
                  ) : (
                    <ButtonGroup>
                      {canLock && (
                        <div
                          onClick={() => handleLock(index)}
                          style={{ cursor: 'pointer', marginRight: 10, display: 'flex', alignItems: 'center' }}
                        >
                          {report.is_locked ? (
                            <FaLock color="red" size={20} />
                          ) : (
                            <FaLockOpen color="green" size={20} />
                          )}
                        </div>
                      )}

                      {canView && (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() =>
                            handleReportNavigation(navigate, `/reports/${report.report_id}`)
                          }
                        >
                          View
                        </Button>
                      )}
                      {canUpdateWater && report.sample_type?.toUpperCase() === 'WATER' && (
                        <Button
                          variant="info"
                          size="sm"
                          className="ms-1"
                          onClick={() => handleOpenWaterModal(report)}
                        >
                          Update
                        </Button>
                      )}
                    </ButtonGroup>
                  )}
                </td>

                {/* GENERIC FOLLOWUP (Support) */}
                {canFollowup && report.sample_type?.toUpperCase() === 'SOIL' && (
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/reports/${report.report_id}/follow-ups`)}
                      className="d-inline-flex align-items-center gap-1 shadow-sm"
                    >
                      <FaClipboardList size={14} />
                      <span>Follow Up</span>
                    </Button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={13} className="no-entries py-4 text-muted">
                No Entries Found..
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

ReportsTable.propTypes = {
  reports: PropTypes.array.isRequired,
  handleLock: PropTypes.func,
};

export default ReportsTable;